import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChevronRightIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

/**
 * PMDataTable — generic, schema-driven list table.
 *
 * Implements the Promang Table View design: multi-select (+ shift range),
 * column show/hide/reorder/resize, grouping with collapsible sections,
 * server-driven sort indicators, density toggle, infinite scroll and a
 * right detail drawer trigger (via onRowClick).
 *
 * Column definition:
 *   {
 *     key,            // unique key
 *     label,          // header label
 *     sortKey,        // optional; enables click-to-sort header (maps to backend field)
 *     width,          // default px width
 *     align,          // 'left' | 'right'
 *     always,         // locked column (cannot be hidden / not draggable)
 *     groupable,      // can be used to group rows
 *     groupValue,     // (row) => key used for grouping
 *     groupLabel,     // (key, rows) => node shown in the group header
 *     cell,           // (row) => ReactNode
 *     stopRowClick,   // if true, clicks inside the cell don't trigger onRowClick
 *   }
 */

const PREFIX = "pmdt";

// Sticky thead uses z-[1] inside this relative z-0 context so it stays above
// scrolling cells without competing with portaled dialogs (PMDialog / Headless UI).

const TABLE_CSS = `
.${PREFIX} .${PREFIX}-tbl tbody tr:hover td { background: rgb(247 248 251); }
.${PREFIX} .${PREFIX}-tbl tbody tr:hover td.${PREFIX}-sticky { background: rgb(247 248 251); }
.${PREFIX} .${PREFIX}-tbl tbody tr.${PREFIX}-sel td { background: rgb(238 242 255); }
.${PREFIX} .${PREFIX}-tbl tbody tr.${PREFIX}-sel td.${PREFIX}-sticky { background: rgb(238 242 255); }
.${PREFIX} .${PREFIX}-tbl tbody tr.${PREFIX}-sel:hover td { background: rgb(232 238 255); }
.${PREFIX} .${PREFIX}-tbl tbody tr.${PREFIX}-sel:hover td.${PREFIX}-sticky { background: rgb(232 238 255); }
.${PREFIX} .${PREFIX}-tbl .${PREFIX}-cbx-wrap { opacity: 0; transition: opacity .1s; }
.${PREFIX} .${PREFIX}-tbl tbody tr:hover .${PREFIX}-cbx-wrap,
.${PREFIX} .${PREFIX}-tbl tbody tr.${PREFIX}-sel .${PREFIX}-cbx-wrap { opacity: 1; }
.${PREFIX} .${PREFIX}-tbl th .${PREFIX}-resize { position: absolute; right: -3px; top: 8px; bottom: 8px; width: 8px; cursor: col-resize; z-index: 5; }
.${PREFIX} .${PREFIX}-tbl th .${PREFIX}-resize:hover::after { content: ''; position: absolute; left: 3px; top: 0; bottom: 0; width: 2px; background: rgb(59 130 246); }
`;

function PMCheckbox({ checked, indeterminate, onChange, title }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      title={title}
      checked={!!checked}
      onChange={onChange}
      className="h-[16px] w-[16px] cursor-pointer rounded-[4px] border-[1.5px] border-slate-300 text-blue-500 accent-blue-500 transition-colors checked:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:ring-offset-0 focus:outline-none"
    />
  );
}

function SkeletonRows({ cols, rows = 8 }) {
  return Array.from({ length: rows }, (_, i) => (
    <tr key={i}>
      <td className="w-11 px-3">
        <div className="mx-auto h-[16px] w-[16px] rounded-[4px] bg-slate-200" />
      </td>
      {Array.from({ length: cols }, (_, j) => (
        <td key={j} className="px-4">
          <div
            className="h-3.5 animate-pulse rounded bg-slate-200"
            style={{ width: j === 0 ? "55%" : j === 1 ? "80%" : "65%" }}
          />
        </td>
      ))}
      <td className="w-11" />
    </tr>
  ));
}

function EmptyState({ cols, title, subtitle }) {
  return (
    <tr>
      <td colSpan={cols + 2} className="py-16 text-center">
        <div className="mx-auto mb-3 flex h-[56px] w-[56px] items-center justify-center rounded-[14px] border border-slate-200 bg-slate-50">
          <svg
            className="h-6 w-6 text-slate-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
            />
          </svg>
        </div>
        <p className="text-[15px] font-semibold text-slate-700">{title}</p>
        <p className="mt-1 text-sm text-slate-400">{subtitle}</p>
      </td>
    </tr>
  );
}

function inferCardRole(col) {
  if (col.cardRole) return col.cardRole;
  if (col.always || col.key === "name") return "title";
  if (col.key === "status" || col.key === "active") return "status";
  return "detail";
}

/** Roles that must stay on the compact card even when hidden from the table. */
const CARD_SURFACE_ROLES = new Set(["title", "status", "meta", "action"]);

function resolveCardColumns(allColumns, visibleCols) {
  const seen = new Set();
  const merged = [];

  visibleCols.forEach((col) => {
    if (!seen.has(col.key)) {
      merged.push(col);
      seen.add(col.key);
    }
  });

  allColumns.forEach((col) => {
    const role = inferCardRole(col);
    if (CARD_SURFACE_ROLES.has(role) && !seen.has(col.key)) {
      merged.push(col);
      seen.add(col.key);
    }
  });

  return merged;
}

/** Split visible columns into title / status / meta / actions vs collapsible details. */
function splitCardColumns(cols) {
  const titleCols = [];
  const statusCols = [];
  const metaCols = [];
  const actionCols = [];
  const detailCols = [];
  cols.forEach((col) => {
    const role = inferCardRole(col);
    if (role === "title") titleCols.push(col);
    else if (role === "status") statusCols.push(col);
    else if (role === "meta") metaCols.push(col);
    else if (role === "action") actionCols.push(col);
    else detailCols.push(col);
  });
  if (!titleCols.length && cols.length) {
    titleCols.push(cols[0]);
    const rest = [statusCols, metaCols, actionCols, detailCols];
    rest.forEach((bucket) => {
      const idx = bucket.findIndex((c) => c.key === cols[0].key);
      if (idx >= 0) bucket.splice(idx, 1);
    });
  }
  return { titleCols, statusCols, metaCols, actionCols, detailCols };
}

function MobileEmptyState({ title, subtitle }) {
  return (
    <div className="px-4 py-14 text-center">
      <div className="mx-auto mb-3 flex h-[52px] w-[52px] items-center justify-center rounded-[14px] border border-slate-200 bg-slate-50">
        <svg
          className="h-6 w-6 text-slate-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
          />
        </svg>
      </div>
      <p className="text-[15px] font-semibold text-slate-700">{title}</p>
      <p className="mt-1 text-sm text-slate-400">{subtitle}</p>
    </div>
  );
}

function MobileSkeletonRows({ rows = 6 }) {
  return (
    <div className="divide-y divide-slate-100">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-start gap-2.5 px-3 py-3">
          <div className="mt-0.5 h-[16px] w-[16px] flex-shrink-0 rounded-[4px] bg-slate-200" />
          <div className="min-w-0 flex-1 space-y-2">
            <div
              className="h-3.5 animate-pulse rounded bg-slate-200"
              style={{ width: i % 2 ? "72%" : "58%" }}
            />
            <div className="h-3 w-2/5 animate-pulse rounded bg-slate-100" />
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 pt-0.5">
              <div className="h-2.5 animate-pulse rounded bg-slate-100" />
              <div className="h-2.5 animate-pulse rounded bg-slate-100" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function renderColCell(col, row) {
  return col.cell ? col.cell(row) : row[col.key] ?? "—";
}

function MobileCardRow({
  row,
  cols,
  selected,
  indent = 0,
  compact,
  onToggle,
  onRowClick,
  toggleTitle,
  hideSelection,
  t,
}) {
  const { titleCols, statusCols, metaCols, actionCols, detailCols } =
    splitCardColumns(cols);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const py = compact ? "py-2.5" : "py-3";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onRowClick?.(row)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onRowClick?.(row);
        }
      }}
      className={`flex cursor-pointer items-start gap-2.5 border-b border-slate-100 px-3 ${py} transition-colors last:border-b-0 hover:bg-slate-50/80 active:bg-slate-100/80 ${
        selected ? "bg-indigo-50/70 hover:bg-indigo-50/80" : ""
      }`}
      style={indent ? { paddingLeft: indent + 12 } : undefined}
    >
      {!hideSelection && (
        <div
          className="mt-0.5 flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <PMCheckbox checked={selected} onChange={onToggle} title={toggleTitle} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1 space-y-0.5">
            {titleCols.map((c) => (
              <div
                key={c.key}
                className="min-w-0"
                onClick={c.stopRowClick ? (e) => e.stopPropagation() : undefined}
              >
                {renderColCell(c, row)}
              </div>
            ))}
          </div>
          {statusCols.length > 0 && (
            <div className="flex max-w-[46%] flex-shrink-0 flex-col items-end gap-1">
              {statusCols.map((c) => (
                <div
                  key={c.key}
                  className="min-w-0"
                  onClick={c.stopRowClick ? (e) => e.stopPropagation() : undefined}
                >
                  {renderColCell(c, row)}
                </div>
              ))}
            </div>
          )}
        </div>
        {metaCols.length > 0 && (
          <div
            className={`${compact ? "mt-1" : "mt-1.5"} flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-slate-500`}
          >
            {metaCols.map((c, i) => (
              <Fragment key={c.key}>
                {i > 0 ? (
                  <span className="text-slate-300" aria-hidden>
                    ·
                  </span>
                ) : null}
                <span
                  className="min-w-0 truncate"
                  onClick={c.stopRowClick ? (e) => e.stopPropagation() : undefined}
                >
                  {renderColCell(c, row)}
                </span>
              </Fragment>
            ))}
          </div>
        )}
        {detailCols.length > 0 && (
          <div className={compact ? "mt-1" : "mt-1.5"}>
            <button
              type="button"
              className="text-[11px] font-semibold text-slate-400 hover:text-slate-600"
              onClick={(e) => {
                e.stopPropagation();
                setDetailsOpen((open) => !open);
              }}
            >
              {detailsOpen
                ? t("module_overview.hide_details")
                : t("module_overview.show_details")}
            </button>
            {detailsOpen && (
              <dl className="mt-1.5 space-y-1">
                {detailCols.map((c) => (
                  <div
                    key={c.key}
                    className="grid grid-cols-[minmax(0,38%)_minmax(0,1fr)] items-baseline gap-x-2"
                    onClick={
                      c.stopRowClick ? (e) => e.stopPropagation() : undefined
                    }
                  >
                    <dt className="truncate text-[10.5px] font-semibold uppercase tracking-[0.04em] text-slate-400">
                      {c.label}
                    </dt>
                    <dd className="min-w-0 text-right text-[13px] text-slate-700">
                      {renderColCell(c, row)}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        )}
        {actionCols.length > 0 && (
          <div
            className={`${compact ? "mt-1.5" : "mt-2"}`}
            onClick={(e) => e.stopPropagation()}
          >
            {actionCols.map((c) => (
              <div key={c.key} className="min-w-0">
                {renderColCell(c, row)}
              </div>
            ))}
          </div>
        )}
      </div>
      <ChevronRightIcon className="mt-1 h-4 w-4 flex-shrink-0 text-slate-300" />
    </div>
  );
}

function MobileCardList({
  cols,
  sections,
  groupCol,
  collapsed,
  toggleGroup,
  selectedIds,
  toggleRow,
  getRowId,
  rowIndent,
  density,
  isLoading,
  rows,
  hasMore,
  emptyTitle,
  emptySubtitle,
  onRowClick,
  toggleTitle,
  selectAllTitle,
  allChecked,
  someChecked,
  toggleAll,
  hideSelection,
  t,
}) {
  const compact = density === "compact";

  if (isLoading && !rows.length) {
    return <MobileSkeletonRows rows={6} />;
  }

  if (!rows.length) {
    return (
      <MobileEmptyState
        title={emptyTitle || t("table_view.empty_title")}
        subtitle={emptySubtitle || t("table_view.empty_subtitle")}
      />
    );
  }

  return (
    <>
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50/90 px-3 py-2">
        {!hideSelection ? (
          <PMCheckbox
            checked={allChecked}
            indeterminate={someChecked}
            onChange={toggleAll}
            title={selectAllTitle}
          />
        ) : (
          <span />
        )}
        <span className="text-[11px] font-semibold uppercase tracking-[0.055em] text-slate-400">
          {t("table_view.rows_shown", { count: rows.length })}
        </span>
      </div>
      <div className="divide-y divide-slate-100">
        {sections.map((sec) => {
          const isCollapsed = collapsed.has(sec.key);
          return (
            <Fragment key={sec.key}>
              {groupCol && (
                <button
                  type="button"
                  onClick={() => toggleGroup(sec.key)}
                  className="flex w-full items-center gap-2 border-b border-slate-100 bg-slate-50/80 px-3 py-2.5 text-left hover:bg-slate-100"
                >
                  <ChevronDownIcon
                    className={`h-4 w-4 flex-shrink-0 text-slate-400 transition-transform ${
                      isCollapsed ? "-rotate-90" : ""
                    }`}
                  />
                  <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-slate-700">
                    {sec.header}
                  </span>
                  <span className="tabular-nums rounded-full border border-slate-200 bg-white px-2 py-px text-[11px] text-slate-400">
                    {sec.rows.length}
                  </span>
                </button>
              )}
              {!isCollapsed &&
                sec.rows.map((row) => {
                  const id = getRowId(row);
                  return (
                    <MobileCardRow
                      key={id}
                      row={row}
                      cols={cols}
                      selected={selectedIds.has(id)}
                      indent={rowIndent ? rowIndent(row) : 0}
                      compact={compact}
                      onToggle={(e) => toggleRow(id, e.nativeEvent.shiftKey)}
                      onRowClick={onRowClick}
                      toggleTitle={toggleTitle}
                      hideSelection={hideSelection}
                      t={t}
                    />
                  );
                })}
            </Fragment>
          );
        })}
      </div>
      {rows.length > 0 && (
        <div className="border-t border-slate-100 py-3 text-center">
          {isLoading ? (
            <span className="inline-flex items-center gap-2 text-[13px] text-slate-400">
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
              {t("table_view.loading_more")}
            </span>
          ) : !hasMore ? (
            <span className="text-[12px] text-slate-300">
              {t("table_view.rows_shown", { count: rows.length })}
            </span>
          ) : null}
        </div>
      )}
    </>
  );
}

export const PMDataTable = ({
  columns = [],
  visibleKeys,
  widths = {},
  onResize,
  groupBy = "none",
  sort = null,
  onSort,
  data = [],
  isLoading = false,
  getRowId = (r) => r.id,
  density = "comfortable",
  hasMore = false,
  onLoadMore,
  onRowClick,
  onSelectionChange,
  reorderRows,
  rowIndent,
  emptyTitle,
  emptySubtitle,
  minWidth = 720,
  mobileRowLayout = false,
  layout = "table",
  hideSelection = false,
}) => {
  const { t } = useTranslation();
  const resolvedLayout =
    layout === "cards" || layout === "responsive"
      ? layout
      : mobileRowLayout
        ? "responsive"
        : "table";
  const showCards = resolvedLayout === "cards" || resolvedLayout === "responsive";
  const showTable = resolvedLayout === "table" || resolvedLayout === "responsive";
  const sentinelRef = useRef(null);
  const resizing = useRef(null);
  const lastIdxRef = useRef(null);

  // Resolve ordered, visible columns
  const cols = useMemo(() => {
    const byKey = new Map(columns.map((c) => [c.key, c]));
    const keys =
      visibleKeys && visibleKeys.length
        ? visibleKeys
        : columns.map((c) => c.key);
    return keys.map((k) => byKey.get(k)).filter(Boolean);
  }, [columns, visibleKeys]);

  const cardCols = useMemo(
    () => (showCards ? resolveCardColumns(columns, cols) : cols),
    [columns, cols, showCards]
  );

  // Optionally reorder rows (e.g. project hierarchy) — disabled while grouping
  const rows = useMemo(() => {
    if (groupBy && groupBy !== "none") return data;
    return reorderRows ? reorderRows(data) : data;
  }, [data, reorderRows, groupBy]);

  // ── Grouping ────────────────────────────────────────────────────────────
  const groupCol = useMemo(
    () => (groupBy && groupBy !== "none" ? columns.find((c) => c.key === groupBy) : null),
    [columns, groupBy]
  );

  const sections = useMemo(() => {
    if (!groupCol) return [{ key: "__all__", rows }];
    const map = new Map();
    rows.forEach((r) => {
      const k = groupCol.groupValue ? groupCol.groupValue(r) : r[groupCol.key];
      const key = k == null || k === "" ? "__none__" : String(k);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], "nb"))
      .map(([key, secRows]) => ({
        key,
        rows: secRows,
        header: groupCol.groupLabel
          ? groupCol.groupLabel(key === "__none__" ? null : key, secRows)
          : key === "__none__"
          ? t("table_view.no_value")
          : key,
      }));
  }, [groupCol, rows, t]);

  const orderedIds = useMemo(
    () => sections.flatMap((s) => s.rows.map((r) => getRowId(r))),
    [sections, getRowId]
  );

  // ── Selection ───────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [collapsed, setCollapsed] = useState(() => new Set());

  useEffect(() => {
    // Drop selections that are no longer present after a data change
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const present = new Set(orderedIds);
      const next = new Set([...prev].filter((id) => present.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [orderedIds]);

  useEffect(() => {
    if (typeof onSelectionChange !== "function") return;
    const all = sections.flatMap((s) => s.rows);
    onSelectionChange(all.filter((r) => selectedIds.has(getRowId(r))));
  }, [selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const allChecked =
    orderedIds.length > 0 && orderedIds.every((id) => selectedIds.has(id));
  const someChecked =
    !allChecked && orderedIds.some((id) => selectedIds.has(id));

  const toggleRow = (id, shift) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const idx = orderedIds.indexOf(id);
      if (shift && lastIdxRef.current != null) {
        const [a, b] = [lastIdxRef.current, idx].sort((x, y) => x - y);
        for (let i = a; i <= b; i++) next.add(orderedIds[i]);
      } else {
        next.has(id) ? next.delete(id) : next.add(id);
        lastIdxRef.current = idx;
      }
      return next;
    });
  };
  const toggleAll = () =>
    setSelectedIds(allChecked ? new Set() : new Set(orderedIds));
  const toggleGroup = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // ── Infinite scroll ─────────────────────────────────────────────────────
  const loadMoreLockedRef = useRef(false);

  useEffect(() => {
    if (!isLoading) {
      loadMoreLockedRef.current = false;
    }
  }, [isLoading]);

  useEffect(() => {
    if (!hasMore || isLoading || !onLoadMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loadMoreLockedRef.current) {
          loadMoreLockedRef.current = true;
          onLoadMore();
        }
      },
      { rootMargin: "160px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, isLoading, onLoadMore]);

  // ── Column resize ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!onResize) return undefined;
    const move = (e) => {
      if (!resizing.current) return;
      const { key, startX, startW } = resizing.current;
      onResize(key, Math.max(80, startW + e.clientX - startX));
    };
    const up = () => {
      if (resizing.current) {
        document.body.style.cursor = "";
        resizing.current = null;
      }
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [onResize]);

  const ROW_H = density === "compact" ? 42 : 60;
  const totalColSpan = cols.length + 2;

  return (
    <>
      <style>{TABLE_CSS}</style>
      <div className={`relative z-0 ${PREFIX}${density === "compact" ? ` ${PREFIX}-compact` : ""}`}>
        {showCards && (
          <div className={resolvedLayout === "responsive" ? "md:hidden" : undefined}>
            <MobileCardList
              cols={cardCols}
              sections={sections}
              groupCol={groupCol}
              collapsed={collapsed}
              toggleGroup={toggleGroup}
              selectedIds={selectedIds}
              toggleRow={toggleRow}
              getRowId={getRowId}
              rowIndent={rowIndent}
              density={density}
              isLoading={isLoading}
              rows={rows}
              hasMore={hasMore}
              emptyTitle={emptyTitle}
              emptySubtitle={emptySubtitle}
              onRowClick={onRowClick}
              toggleTitle={t("Select")}
              selectAllTitle={t("Select all")}
              allChecked={allChecked}
              someChecked={someChecked}
              toggleAll={toggleAll}
              hideSelection={hideSelection}
              t={t}
            />
          </div>
        )}
        {showTable && (
        <div className={resolvedLayout === "responsive" ? "hidden overflow-x-auto md:block" : "overflow-x-auto"}>
          <table className={`${PREFIX}-tbl w-full border-collapse`} style={{ minWidth }}>
            <colgroup>
              <col style={{ width: 44 }} />
              {cols.map((c) => (
                <col key={c.key} style={{ width: widths[c.key] || c.width || undefined }} />
              ))}
              <col style={{ width: 44 }} />
            </colgroup>

            <thead>
              <tr>
                <th
                  scope="col"
                  className="sticky top-0 z-[1] w-11 border-b border-slate-200 bg-slate-50 px-3 text-center"
                  style={{ height: 42 }}
                >
                  <PMCheckbox
                    checked={allChecked}
                    indeterminate={someChecked}
                    onChange={toggleAll}
                    title={t("Select all")}
                  />
                </th>
                {cols.map((c) => {
                  const active = c.sortKey && sort?.field === c.sortKey;
                  return (
                    <th
                      key={c.key}
                      scope="col"
                      className="relative sticky top-0 z-[1] border-b border-slate-200 bg-slate-50 px-4 py-0 text-[11px] font-semibold uppercase tracking-[0.055em] text-slate-400 whitespace-nowrap select-none"
                      style={{ height: 42, textAlign: c.align === "right" ? "right" : "left" }}
                    >
                      {c.sortKey && onSort ? (
                        <button
                          type="button"
                          onClick={() => onSort(c.sortKey)}
                          className="inline-flex h-full items-center gap-1.5 hover:text-slate-700"
                          style={{ flexDirection: c.align === "right" ? "row-reverse" : "row" }}
                        >
                          {c.label}
                          {active && (
                            <span className="text-slate-900">
                              {sort.dir === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </button>
                      ) : (
                        c.label
                      )}
                      {onResize && (
                        <span
                          className={`${PREFIX}-resize`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            document.body.style.cursor = "col-resize";
                            resizing.current = {
                              key: c.key,
                              startX: e.clientX,
                              startW:
                                widths[c.key] ||
                                c.width ||
                                e.currentTarget.parentElement.offsetWidth,
                            };
                          }}
                        />
                      )}
                    </th>
                  );
                })}
                <th
                  className="sticky top-0 z-[1] w-11 border-b border-slate-200 bg-slate-50"
                  style={{ height: 42 }}
                />
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {isLoading && !data.length ? (
                <SkeletonRows cols={cols.length} rows={8} />
              ) : !rows.length ? (
                <EmptyState
                  cols={cols.length}
                  title={emptyTitle || t("table_view.empty_title")}
                  subtitle={emptySubtitle || t("table_view.empty_subtitle")}
                />
              ) : (
                sections.map((sec) => {
                  const isCollapsed = collapsed.has(sec.key);
                  return (
                    <Fragment key={sec.key}>
                      {groupCol && (
                        <tr className="bg-slate-50/80">
                          <td colSpan={totalColSpan} className="p-0">
                            <button
                              type="button"
                              onClick={() => toggleGroup(sec.key)}
                              className="flex h-10 w-full items-center gap-2.5 px-4 text-left hover:bg-slate-100"
                            >
                              <ChevronDownIcon
                                className={`h-4 w-4 text-slate-400 transition-transform ${
                                  isCollapsed ? "-rotate-90" : ""
                                }`}
                              />
                              <span className="inline-flex items-center gap-2 text-[13.5px] font-semibold text-slate-700">
                                {sec.header}
                              </span>
                              <span className="tabular-nums rounded-full border border-slate-200 bg-white px-2 py-px text-[12px] text-slate-400">
                                {sec.rows.length}
                              </span>
                            </button>
                          </td>
                        </tr>
                      )}
                      {!isCollapsed &&
                        sec.rows.map((row) => {
                          const id = getRowId(row);
                          const sel = selectedIds.has(id);
                          const indent = rowIndent ? rowIndent(row) : 0;
                          return (
                            <tr
                              key={id}
                              className={sel ? `${PREFIX}-sel cursor-pointer` : "cursor-pointer"}
                              onClick={() => onRowClick?.(row)}
                            >
                              <td
                                className={`${PREFIX}-sticky w-11 px-3 text-center`}
                                style={{ height: ROW_H, background: "inherit" }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className={`${PREFIX}-cbx-wrap mx-auto w-fit`}>
                                  <PMCheckbox
                                    checked={sel}
                                    onChange={(e) =>
                                      toggleRow(id, e.nativeEvent.shiftKey)
                                    }
                                    title={t("Select")}
                                  />
                                </div>
                              </td>
                              {cols.map((c, ci) => (
                                <td
                                  key={c.key}
                                  className="px-4 text-[13.5px] text-slate-700"
                                  style={{
                                    height: ROW_H,
                                    textAlign: c.align === "right" ? "right" : "left",
                                    paddingLeft:
                                      ci === 0 && indent ? indent + 16 : undefined,
                                  }}
                                  onClick={
                                    c.stopRowClick
                                      ? (e) => e.stopPropagation()
                                      : undefined
                                  }
                                >
                                  {c.cell ? c.cell(row) : row[c.key] ?? "—"}
                                </td>
                              ))}
                              <td className="w-11 text-center" style={{ height: ROW_H }}>
                                <ChevronRightIcon className="mx-auto h-4 w-4 text-slate-300" />
                              </td>
                            </tr>
                          );
                        })}
                    </Fragment>
                  );
                })
              )}
            </tbody>

            {rows.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={totalColSpan} className="py-3 text-center">
                    {isLoading ? (
                      <span className="inline-flex items-center gap-2 text-[13px] text-slate-400">
                        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="3"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          />
                        </svg>
                        {t("table_view.loading_more")}
                      </span>
                    ) : !hasMore ? (
                      <span className="text-[12px] text-slate-300">
                        {t("table_view.rows_shown", { count: rows.length })}
                      </span>
                    ) : null}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        )}
        <div ref={sentinelRef} className="h-4" aria-hidden="true" />
      </div>
    </>
  );
};

export default PMDataTable;
